"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

/* ──────────────────────────────────────────
   1. Co-Create Together — mini chat exchange
   ────────────────────────────────────────── */

export function CoCreateAnimation() {
  const { t } = useTranslation();
  const [loopKey, setLoopKey] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setLoopKey((k) => k + 1), 10000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="w-full h-56 md:h-56 bg-white/[0.03] rounded-lg overflow-hidden flex flex-col justify-end p-3 md:p-4 gap-2 md:gap-3 relative"
      aria-hidden="true"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={loopKey}
          className="flex flex-col gap-2 md:gap-3"
          exit={{ opacity: 0, transition: { duration: 0.4 } }}
        >
          {/* User message */}
          <motion.div
            className="flex justify-end"
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0, duration: 0.3 }}
          >
            <div className="bg-black/60 rounded-lg px-2.5 md:px-3 py-2 md:py-2.5 max-w-[85%]">
              <span className="text-[8px] md:text-[9px] font-medium text-white/50 block mb-1">{t("you")}</span>
              <p className="text-[9px] md:text-[11px] leading-relaxed text-white/70">
                {t("landing.animation_cocreate_user")}
              </p>
            </div>
          </motion.div>

          {/* Lema reply */}
          <motion.div
            className="flex justify-start"
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5, duration: 0.3 }}
          >
            <div className="bg-white/[0.06] border border-highlight/10 rounded-lg px-2.5 md:px-3 py-2 md:py-2.5 max-w-[92%]">
              <span className="text-[8px] md:text-[9px] font-medium text-highlight/70 italic block mb-1">Lema</span>
              <p className="text-[9px] md:text-[11px] leading-relaxed text-white/70 mb-1.5 md:mb-2">
                {t("landing.animation_cocreate_lema_before")}<span className="text-highlight/80">{t("landing.animation_cocreate_lema_highlight")}</span>{t("landing.animation_cocreate_lema_after")}
              </p>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 bg-white/[0.05] rounded px-2 py-1 md:py-1.5">
                  <span className="text-[8px] md:text-[9px] font-bold text-highlight/60">1.</span>
                  <span className="text-[8px] md:text-[10px] text-white/50">{t("landing.animation_cocreate_tip1")}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/[0.05] rounded px-2 py-1 md:py-1.5">
                  <span className="text-[8px] md:text-[9px] font-bold text-highlight/60">2.</span>
                  <span className="text-[8px] md:text-[10px] text-white/50">{t("landing.animation_cocreate_tip2")}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ──────────────────────────────────────────
   2. Understand Your Data — charts → insights
   ────────────────────────────────────────── */

const INSIGHT_KEYS = [
  [
    { sign: "+", key: "landing.animation.views_up", positive: true },
    { sign: "–", key: "landing.animation.shares_dip", positive: false },
  ],
  [
    { sign: "+", key: "landing.animation.saves_spike", positive: true },
    { sign: "–", key: "landing.animation.reach_drops", positive: false },
  ],
  [
    { sign: "+", key: "landing.animation.comments_up", positive: true },
    { sign: "–", key: "landing.animation.likes_dip", positive: false },
  ],
];

const VIDEO_VARIANTS = [
  {
    bg: "linear-gradient(to bottom right, rgba(99,102,241,0.3), rgba(139,92,246,0.2))",
    timestamp: "0:15",
    shimmer: ["60%", "80%"],
  },
  {
    bg: "linear-gradient(to bottom right, rgba(20,184,166,0.3), rgba(34,211,238,0.2))",
    timestamp: "0:42",
    shimmer: ["40%", "65%"],
  },
  {
    bg: "linear-gradient(to bottom right, rgba(249,115,22,0.25), rgba(251,191,36,0.2))",
    timestamp: "1:08",
    shimmer: ["75%", "50%"],
  },
];

export function UnderstandDataAnimation() {
  const { t } = useTranslation();
  const [setIdx, setSetIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setSetIdx((i) => (i + 1) % INSIGHT_KEYS.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="w-full h-48 md:h-56 bg-white/[0.03] rounded-lg overflow-hidden flex items-center justify-center gap-4 px-4 md:px-5 relative"
      aria-hidden="true"
    >
      {/* Mini Reels frame */}
      <div className="relative w-14 md:w-16 h-24 md:h-28 flex-shrink-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={setIdx}
            className="absolute inset-0 rounded-lg border border-white/10 flex items-center justify-center overflow-hidden"
            style={{ background: VIDEO_VARIANTS[setIdx].bg }}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.35 }}
          >
            {/* Shimmer lines (video content hint) */}
            <div className="absolute top-2.5 left-2 right-2 flex flex-col gap-1">
              <div className="h-0.5 bg-white/10 rounded-full" style={{ width: VIDEO_VARIANTS[setIdx].shimmer[0] }} />
              <div className="h-0.5 bg-white/10 rounded-full" style={{ width: VIDEO_VARIANTS[setIdx].shimmer[1] }} />
            </div>
            {/* Play button */}
            <div className="w-7 h-7 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <svg width="8" height="10" viewBox="0 0 8 10" fill="white" fillOpacity="0.6">
                <path d="M0 0L8 5L0 10V0Z" />
              </svg>
            </div>
            {/* Duration badge */}
            <div className="absolute bottom-1.5 right-1 bg-black/50 rounded px-1 py-0.5 text-[7px] md:text-[8px] text-white/60 font-mono">
              {VIDEO_VARIANTS[setIdx].timestamp}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Arrow */}
      <svg width="20" height="20" viewBox="0 0 20 20" className="opacity-25 flex-shrink-0">
        <path
          d="M4 10H16M12 6l4 4-4 4"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>

      {/* Insight rows */}
      <div className="flex flex-col gap-2 min-w-0 flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={setIdx}
            className="flex flex-col gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
          >
            {INSIGHT_KEYS[setIdx].map((insight, i) => (
              <motion.div
                key={i}
                className="flex items-center gap-2 bg-white/[0.06] rounded-lg px-2.5 py-2"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.12 }}
              >
                <span
                  className={`text-sm font-bold leading-none flex-shrink-0 ${
                    insight.positive ? "text-green-400" : "text-red-400"
                  }`}
                >
                  {insight.sign}
                </span>
                <span className="text-[11px] md:text-xs text-white/60 leading-snug">
                  {t(insight.key)}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   3. Be There For Your Journey — always-on beacon
   ────────────────────────────────────────── */

const FLOATING_EMOJIS = [
  { emoji: "❤️",  left: "14%", bottom: "18%", delay: 0,   duration: 3.8 },
  { emoji: "👍",  left: "75%", bottom: "22%", delay: 1.4, duration: 3.4 },
  { emoji: "🥰",  left: "52%", bottom: "14%", delay: 2.6, duration: 4.2 },
  { emoji: "✨",  left: "30%", bottom: "20%", delay: 0.7, duration: 3.6 },
  { emoji: "💜",  left: "65%", bottom: "16%", delay: 3.2, duration: 4.0 },
  { emoji: "😊",  left: "22%", bottom: "26%", delay: 1.9, duration: 3.5 },
];

export function AlwaysThereAnimation() {
  return (
    <div
      className="w-full h-48 md:h-56 bg-white/[0.03] rounded-lg overflow-hidden flex items-center justify-center relative"
      aria-hidden="true"
    >
      {/* Pulsing rings */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border border-highlight/20"
          style={{
            width: 60 + i * 50,
            height: 60 + i * 50,
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.08, 0.3],
          }}
          transition={{
            repeat: Infinity,
            duration: 3,
            delay: i * 0.8,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Floating emojis */}
      {FLOATING_EMOJIS.map(({ emoji, left, bottom, delay, duration }) => (
        <motion.span
          key={emoji}
          className="absolute text-base select-none pointer-events-none"
          style={{ left, bottom }}
          animate={{
            y: [0, -70, -90],
            opacity: [0, 0.9, 0],
            scale: [0.6, 1.1, 0.8],
          }}
          transition={{
            repeat: Infinity,
            duration,
            delay,
            ease: "easeOut",
            times: [0, 0.55, 1],
          }}
        >
          {emoji}
        </motion.span>
      ))}

      {/* Center dot */}
      <motion.div
        className="relative z-10 w-12 h-12 rounded-full bg-gradient-to-br from-highlight/40 to-indigo-500/40 flex items-center justify-center"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgb(196,181,253)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </motion.div>

      {/* Floating time indicators */}
      {["9am", "2pm", "11pm"].map((time, i) => {
        const positions = [
          { top: "18%", left: "20%" },
          { top: "25%", right: "18%" },
          { bottom: "22%", left: "28%" },
        ];
        return (
          <motion.div
            key={time}
            className="absolute text-[10px] text-white/30 font-mono"
            style={positions[i]}
            animate={{
              opacity: [0.15, 0.5, 0.15],
            }}
            transition={{
              repeat: Infinity,
              duration: 4,
              delay: i * 1.3,
              ease: "easeInOut",
            }}
          >
            {time}
          </motion.div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────
   4. Trained on Top Creators — pattern learner
   ────────────────────────────────────────── */

const INDUSTRY_SIGNALS = [
  {
    headerKey: "landing.vs_patterns_industry_cooking",
    patterns: [
      { labelKey: "landing.vs_pattern_cooking_1", lift: "+233%" },
      { labelKey: "landing.vs_pattern_cooking_2", lift: "+108%" },
      { labelKey: "landing.vs_pattern_cooking_3", lift: "+90%" },
    ],
  },
  {
    headerKey: "landing.vs_patterns_industry_beauty",
    patterns: [
      { labelKey: "landing.vs_pattern_beauty_1", lift: "+180%" },
      { labelKey: "landing.vs_pattern_beauty_2", lift: "+145%" },
      { labelKey: "landing.vs_pattern_beauty_3", lift: "+112%" },
    ],
  },
  {
    headerKey: "landing.vs_patterns_industry_travel",
    patterns: [
      { labelKey: "landing.vs_pattern_travel_1", lift: "+160%" },
      { labelKey: "landing.vs_pattern_travel_2", lift: "+128%" },
      { labelKey: "landing.vs_pattern_travel_3", lift: "+94%" },
    ],
  },
  {
    headerKey: "landing.vs_patterns_industry_fitness",
    patterns: [
      { labelKey: "landing.vs_pattern_fitness_1", lift: "+210%" },
      { labelKey: "landing.vs_pattern_fitness_2", lift: "+155%" },
      { labelKey: "landing.vs_pattern_fitness_3", lift: "+108%" },
    ],
  },
  {
    headerKey: "landing.vs_patterns_industry_lifestyle",
    patterns: [
      { labelKey: "landing.vs_pattern_lifestyle_1", lift: "+175%" },
      { labelKey: "landing.vs_pattern_lifestyle_2", lift: "+143%" },
      { labelKey: "landing.vs_pattern_lifestyle_3", lift: "+120%" },
    ],
  },
];

const TOTAL_STEPS = INDUSTRY_SIGNALS.length * 3;

export function CreatorTrainingAnimation() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  const industryIdx = Math.floor(step / 3);
  const patternIdx = step % 3;
  const currentIndustry = INDUSTRY_SIGNALS[industryIdx];

  useEffect(() => {
    const timer = setInterval(() => setStep((s) => (s + 1) % TOTAL_STEPS), 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="w-full h-48 md:h-56 bg-white/[0.03] rounded-lg overflow-hidden flex flex-col justify-center p-4 gap-3"
      aria-hidden="true"
    >
      <div className="flex items-center gap-2 mb-1">
        <svg width="12" height="12" viewBox="0 0 12 12" className="text-yellow-400 flex-shrink-0" fill="currentColor">
          <path d="M6 0L7.4 4.2H12L8.3 6.8L9.7 11L6 8.4L2.3 11L3.7 6.8L0 4.2H4.6L6 0Z" />
        </svg>
        <span className="text-[10px] text-white/40 font-medium uppercase tracking-wide">
          {t(currentIndustry.headerKey)}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {currentIndustry.patterns.map((pattern, i) => (
          <motion.div
            key={i}
            className="flex items-center justify-between rounded-lg px-3 py-2 border"
            animate={{
              backgroundColor: i === patternIdx ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
              borderColor: i === patternIdx ? "rgba(139,92,246,0.3)" : "rgba(255,255,255,0.05)",
              opacity: i === patternIdx ? 1 : 0.45,
            }}
            transition={{ duration: 0.4 }}
          >
            <span className="text-[10px] md:text-xs text-white/70">{t(pattern.labelKey)}</span>
            <span className="text-[10px] md:text-xs font-bold text-green-400 ml-2 flex-shrink-0">{pattern.lift}</span>
          </motion.div>
        ))}
      </div>

      <div className="flex items-center gap-1.5 mt-1">
        <motion.div
          className="w-1.5 h-1.5 rounded-full bg-highlight/60"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        />
        <span className="text-[9px] text-white/25">{t("landing.vs_animation_patterns_footer")}</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   5. Actually Watches Your Videos — scan + tag
   ────────────────────────────────────────── */

const VIDEO_TAG_KEYS = [
  "landing.vs_animation_video_tag_1",
  "landing.vs_animation_video_tag_2",
  "landing.vs_animation_video_tag_3",
];

export function VideoWatchAnimation() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"idle" | "watching" | "done">("idle");
  const [tagCount, setTagCount] = useState(0);
  const [loopKey, setLoopKey] = useState(0);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    setPhase("idle");
    setTagCount(0);
    timeouts.push(setTimeout(() => setPhase("watching"), 800));
    timeouts.push(setTimeout(() => { setPhase("done"); setTagCount(1); }, 2400));
    timeouts.push(setTimeout(() => setTagCount(2), 3100));
    timeouts.push(setTimeout(() => setTagCount(3), 3800));
    timeouts.push(setTimeout(() => setLoopKey((k) => k + 1), 7500));
    return () => timeouts.forEach(clearTimeout);
  }, [loopKey]);

  return (
    <div
      className="w-full h-48 md:h-56 bg-white/[0.03] rounded-lg overflow-hidden flex items-center gap-3 px-4"
      aria-hidden="true"
    >
      {/* Video frame */}
      <div className="relative w-20 md:w-24 h-28 md:h-32 rounded-lg bg-gradient-to-br from-indigo-500/20 to-highlight/10 border border-white/10 flex-shrink-0 overflow-hidden">
        {phase === "watching" && (
          <motion.div
            className="absolute left-0 right-0 h-0.5 bg-highlight/50"
            initial={{ top: 0 }}
            animate={{ top: "100%" }}
            transition={{ duration: 1.4, ease: "linear" }}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <svg width="8" height="10" viewBox="0 0 8 10" fill="white" fillOpacity="0.5">
              <path d="M0 0L8 5L0 10V0Z" />
            </svg>
          </div>
        </div>
        <div className="absolute bottom-1.5 left-1 right-1 flex justify-center">
          <AnimatePresence mode="wait">
            {phase === "watching" && (
              <motion.div
                key="watching"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-black/60 rounded px-1.5 py-0.5 flex items-center gap-1"
              >
                <motion.div
                  className="w-1 h-1 rounded-full bg-red-400"
                  animate={{ opacity: [1, 0.2, 1] }}
                  transition={{ repeat: Infinity, duration: 0.8 }}
                />
                <span className="text-[7px] text-white/60">{t("landing.vs_animation_video_watching")}</span>
              </motion.div>
            )}
            {phase === "done" && (
              <motion.div
                key="done"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-green-500/20 border border-green-500/30 rounded px-1 py-0.5 flex items-center"
              >
                <span className="text-[7px] text-green-400">{t("landing.vs_animation_video_done")}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Arrow */}
      <svg width="16" height="16" viewBox="0 0 16 16" className="opacity-20 flex-shrink-0">
        <path d="M3 8H13M9 4l4 4-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>

      {/* Tags */}
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        {VIDEO_TAG_KEYS.map((key, i) => (
          <AnimatePresence key={i}>
            {tagCount > i && (
              <motion.div
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-1.5 bg-white/[0.06] rounded-lg px-2.5 py-1.5 border border-white/5"
              >
                <span className="w-1 h-1 rounded-full bg-highlight/60 flex-shrink-0" />
                <span className="text-[10px] md:text-xs text-white/60">{t(key)}</span>
              </motion.div>
            )}
          </AnimatePresence>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   6. Knows Your Account — personalized insight
   ────────────────────────────────────────── */

const PERSONAL_INSIGHT_KEYS = [
  "landing.vs_animation_personal_insight_text",
  "landing.vs_animation_personal_insight_text_2",
  "landing.vs_animation_personal_insight_text_3",
];

export function PersonalizedDataAnimation() {
  const { t } = useTranslation();
  const [insightIdx, setInsightIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setInsightIdx((i) => (i + 1) % PERSONAL_INSIGHT_KEYS.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="w-full h-48 md:h-56 bg-white/[0.03] rounded-lg overflow-hidden flex flex-col justify-center p-4 gap-3"
      aria-hidden="true"
    >
      {/* Creator profile — social media style */}
      <div className="flex items-center justify-center gap-2.5">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/40 to-highlight/30 border border-white/10 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-[#0d0d14]" />
        </div>

        {/* Profile info */}
        <div className="flex flex-col gap-1">
          {/* Name */}
          <div className="h-2 w-20 bg-white/20 rounded-full" />
          {/* Handle */}
          <div className="h-1.5 w-14 bg-white/10 rounded-full" />
          {/* Stats */}
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[7px] text-white/20 font-mono">
              {t("landing.vs_animation_personal_followers", { count: "12.4K", defaultValue: "12.4K followers" })}
            </span>
          </div>
        </div>
      </div>

      {/* Cycling insight card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={insightIdx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4 }}
          className="bg-white/[0.06] rounded-lg px-3 py-2.5 border border-white/5"
        >
          <span className="text-[8px] font-medium text-highlight/50 uppercase tracking-wide block mb-1">
            {t("landing.vs_animation_personal_insight_label")}
          </span>
          <p className="text-[10px] md:text-xs text-white/70 leading-relaxed">
            {t(PERSONAL_INSIGHT_KEYS[insightIdx])}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* Dot indicators */}
      <div className="flex items-center gap-1.5 justify-center">
        {PERSONAL_INSIGHT_KEYS.map((_, i) => (
          <motion.div
            key={i}
            className="rounded-full bg-white/40"
            animate={{
              width: i === insightIdx ? 14 : 4,
              height: 4,
              opacity: i === insightIdx ? 0.7 : 0.2,
            }}
            transition={{ duration: 0.35 }}
          />
        ))}
      </div>
    </div>
  );
}
